import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  getListProductByMedicineKey,
  getMedicineNameKey,
  getMedicinePieChartKey,
  getMedicinesByRangeKey,
  getMedicinesKey,
  getMedicineSummaryTodayKey,
  getRecentMedicineKey,
} from 'common/constants/queryKeys'
import useCycleApi from 'common/hooks/useCycleApi'
import { BasePaginationResponseV2, BaseResponse } from 'common/types/api'
import { formatDate } from 'common/utils/date'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import { DisplayMode } from 'modules/water/api/types'
import { useParams } from 'react-router'
import { useRecoilValue } from 'recoil'

import { getListProductByMedicine, MEDICINE_REQUEST_CONFIG } from '.'
import { medicalSupplyDateFilterState, medicalSupplyTableDateFilterState } from '../stores/atom'
import { Medicine, MedicineName, MedicinePieChart, MedicineSummary, RecentMedicine } from './types'

const ROWS_PER_PAGE = 10

export const useGetMedicines = (mode: DisplayMode) => {
  const cycleApi = useCycleApi()
  const recoilState =
    mode === 'card' ? medicalSupplyDateFilterState : medicalSupplyTableDateFilterState
  const date = useRecoilValue(recoilState)
  const cycleId = useRecoilValue(selectedCycleIdState)
  const initParams = {
    params: {
      pageSize: ROWS_PER_PAGE,
      page: 1,
      date: date ? formatDate(date) : '',
    },
    cycleId,
  }

  return useInfiniteQuery({
    queryKey: getMedicinesKey.list(cycleId || '', date),
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await cycleApi.request<BasePaginationResponseV2<Medicine[]>>({
        ...MEDICINE_REQUEST_CONFIG.get,
        ...pageParam,
      })
      return {
        medicines: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage, _) => {
      if (lastPage.nextPage) {
        return {
          cycleId: initParams.cycleId,
          params: { ...initParams.params, page: lastPage.nextPage },
        }
      } else {
        return undefined
      }
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetMedicinesByRange = (mode: DisplayMode, startDate?: Date, endDate?: Date) => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const recoilState =
    mode === 'card' ? medicalSupplyDateFilterState : medicalSupplyTableDateFilterState
  const date = useRecoilValue(recoilState)
  const initParams = {
    params: {
      pageSize: ROWS_PER_PAGE,
      page: 1,
      start_date: startDate ? formatDate(startDate) : '',
      end_date: endDate ? formatDate(endDate) : '',
    },
    cycleId,
  }

  return useInfiniteQuery({
    queryKey: getMedicinesByRangeKey.list(cycleId || '', startDate, endDate),
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await cycleApi.request<BasePaginationResponseV2<Medicine[]>>({
        ...MEDICINE_REQUEST_CONFIG.get,
        ...pageParam,
      })
      return {
        medicines: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage, _) => {
      if (lastPage.nextPage) {
        return {
          cycleId: initParams.cycleId,
          params: { ...initParams.params, page: lastPage.nextPage },
        }
      } else {
        return undefined
      }
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetMedicineNames = () => {
  const cycleApi = useCycleApi()
  const { farmId } = useParams()

  return useQuery({
    queryKey: getMedicineNameKey.list(farmId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<MedicineName[]>>(
        MEDICINE_REQUEST_CONFIG.getName
      )
      return data.data
    },
  })
}

export const useGetMedicineSummaryByToday = () => {
  const cycleApi = useCycleApi()
  const { farmId } = useParams()

  return useQuery({
    queryKey: getMedicineSummaryTodayKey.list(farmId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<MedicineSummary[]>>(
        MEDICINE_REQUEST_CONFIG.getSummaryToday
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!farmId,
  })
}

export const useGetMedicinePieChart = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getMedicinePieChartKey.details(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<MedicinePieChart[]>>(
        MEDICINE_REQUEST_CONFIG.getPieChart
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetRecentMedicine = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getRecentMedicineKey.details(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<RecentMedicine>>(
        MEDICINE_REQUEST_CONFIG.getRecent
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetListProductByMedicine = () => {
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { pondId, farmId } = useParams()

  return useQuery({
    queryKey: getListProductByMedicineKey.list(farmId || '', pondId || '', cycleId || ''),
    queryFn: async () => {
      const { data } = await getListProductByMedicine(farmId ?? '', pondId ?? '', cycleId ?? '')
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
    refetchOnMount: 'always',
  })
}
